import React, { Component } from 'react'
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ReactSortable } from "react-sortablejs";

import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import Icon from '@mui/material/Icon'
import Paper from '@mui/material/Paper'
import TableUI from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel';

import DateFormat from 'cityads-v3/components/Format/DateFormat';
import NumberFormat, { FORMAT_NUMBER, FORMAT_PERCENT, FORMAT_CURRENCY } from 'cityads-v3/components/Format/NumberFormat';
import Tooltip from 'cityads-v3/components/Tooltip/Tooltip';

import classes from './Table.scss';

const Wrapper = props => {
  const { paper, ...rest } = props;

  return paper ? <Paper {...paper} {...rest} /> : <div {...rest} />;
}

const InnerWrapper = ({ innerWrapper, innerScrollX, ...rest }) => {
  const { className, ...otherProps } = innerWrapper || {};

  return (innerWrapper || innerScrollX) ?
    (
      <div className={classNames(className, { [classes.scrollX]: innerScrollX })} {...otherProps} {...rest} />
    )
    :
    (
      <React.Fragment {...rest} />
    )
  ;
}

export default class Table extends Component {

  static propTypes = {
    cols: PropTypes.array,
    rows: PropTypes.array,
    sortColumn: PropTypes.string,
    sortDirection: PropTypes.oneOf(['asc', 'desc']),
    onRequestSort: PropTypes.func,
    tableHeadClasses: PropTypes.object,

    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    showRowHover: PropTypes.bool,
    onCellClick: PropTypes.func,
    onRowClick: PropTypes.func,
    onRowMouseOver: PropTypes.func,
    onRowMouseOut: PropTypes.func,
    paginatorComponent: PropTypes.any,
    blueTheme: PropTypes.bool,
    scrollX: PropTypes.bool,
    innerScrollX: PropTypes.bool,
    isSmall: PropTypes.bool,
    ReactSortableProps: PropTypes.object
  }

  static defaultProps = {
    isSmall: true
  }

  get cols() {
    const { ReactSortableProps } = this.props

    if (ReactSortableProps) {
      return [
        {
          name: '_sort',
          title: '',
          formatter: () => <Icon classes={{ root: classes.sortHandler }}><DragIndicatorIcon /></Icon>
        },
        ...(this.props.cols || []).filter(col => col),
      ]
    }

    return (this.props.cols || []).filter(col => col);
  }

  render() {
    const {
      paper,
      innerWrapper,
      title,
      onRowClick,
      onRowMouseOver,
      onRowMouseOut,
      paginatorComponent,
      showRowHover,
      blueTheme,
      scrollX,
      innerScrollX,
      sortColumn,
      sortDirection,
      onRequestSort,
      tableHeadClasses,
      isSmall,
      ...rest
    } = this.props;

    return (
      <Wrapper className={classNames({[classes.scrollX]: scrollX})} paper={paper} >
        {
          title && (<div>{title}</div>)
        }
        <InnerWrapper innerWrapper={innerWrapper} innerScrollX={innerScrollX} >
          <TableUI
            {...rest}
          >
            {this.renderHeader()}
            {this.renderBody()}
          </TableUI>
        </InnerWrapper>
        {this.renderPaginator()}
      </Wrapper>
    );
  }

  renderHeader() {
    if(this.cols.filter(item => item.title).length === 0) {
      return;
    }

    const { blueTheme, sortColumn, sortDirection, onRequestSort, tableHeadClasses } = this.props;

    const cols = this.cols.map(col => {
      const content = col.tooltip ?
        (
          <Tooltip title={col.tooltip}><span>{col.title}</span></Tooltip>
        ) :
        col.title;

      return (
        <TableCell
          key={col.name}
          size={col.dense ? 'small' : undefined}
          sortDirection={sortColumn === col.name ? sortDirection : null}
          classes={{
            ...col.headClasses,
            root: classNames(
              classes.cellRoot,
              { [classes.cellRootIsSmall]: this.props.isSmall },
              { [classes.cellRootBlue]: blueTheme },
              col.headClasses?.root,
            ),
            sizeSmall: classNames(classes.cellSizeSmall, col.headClasses?.sizeSmall),
          }}
          style={col.style}
          scope={col.scope}
          component={col.scope ? 'th' : undefined}
          padding={col.padding}
          sx={col.sx}
        >
          {col.sortable ?
            (
              <TableSortLabel
                active={sortColumn === col.name}
                direction={sortColumn === col.name ? sortDirection : 'asc'}
                onClick={onRequestSort ? evt => onRequestSort(evt, col.name) : null}
                classes={{
                  root: classNames({
                    [classes.cursorDefault]: !!col.fixedSort
                  })
                }}
              >
                {content}
              </TableSortLabel>
            ) :
            content
          }
        </TableCell>
      );
    });

    return (
      <TableHead
        classes={tableHeadClasses}
      >
        <TableRow>
          {cols}
        </TableRow>
      </TableHead>
    );
  }

  renderBody() {
    const { ReactSortableProps } = this.props;
    const rows = (this.props.rows || []).map((row, idx) => this.renderRow(row, idx));

    if (ReactSortableProps) {
      return (
        <ReactSortable
          tag={TableBody}
          list={rows}
          handle={`.${classes.sortHandler}`}
          {...ReactSortableProps}
        >
          {rows}
        </ReactSortable>
      )
    }

    return (
      <TableBody>
        {rows}
      </TableBody>
    )
  }

  renderRow(row, num) {
    const cols = this.cols.map(col => {
      let value = row[col.name];

      if(typeof col.formatter === 'function') {
        value = col.formatter(value, row);
      }

      switch (col.format) {
        default: break;
        case 'date':
          value = <DateFormat date={value} />;
          break;
        case 'currency':
          value = <NumberFormat number={value} precision={col.precision} format={FORMAT_CURRENCY} currency={col.currencyCodeField ? row[col.currencyCodeField] : +row[col.currencyField]} />;
          break;
        case 'number':
          value = <NumberFormat number={value} precision={col.precision} format={FORMAT_NUMBER} />;
          break;
        case 'percent':
          value = <NumberFormat number={value} precision={col.precision} format={FORMAT_PERCENT} />;
          break;
      }

      return (
        <TableCell
          key={col.name + '_' + (row.key || row.id || num)}
          size={col.dense ? 'small' : undefined}
          classes={{
            ...col.classes,
            root: classNames(
              classes.cellRoot,
              { [classes.cellRootIsSmall]: this.props.isSmall },
              col.classes?.root,
              col.getClassName?.(row)
            ),
            sizeSmall: classNames(classes.cellSizeSmall, col.classes?.sizeSmall),
          }}
          padding={col.padding}
        >{value}</TableCell>
      );
    });

    return (
      <TableRow
        key={row.key || row.id || num}
        onClick={this.props.onRowClick ? (evt) => this.props.onRowClick(row, evt) : undefined}
        onMouseOver={this.props.onRowMouseOver ? (evt) => this.props.onRowMouseOver(row, evt) : undefined}
        onMouseOut={this.props.onRowMouseOut ? (evt) => this.props.onRowMouseOut(row, evt) : undefined}
        style={row._style}
        hover={row._showRowHover ?? this.props.showRowHover ?? false}
        classes={{
          hover: this.props.onRowClick ? classes.rowHoverAction : undefined,
          ...row.classes,
        }}
      >
        {cols}
      </TableRow>
    );
  }

  renderPaginator() {
    return this.props.paginatorComponent;
  }

}
